import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './banner.module.css';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';

type BannerType = 'info' | 'warning' | 'error' | 'lock' | 'success';

type Icon = 'filled' | 'outlined' | 'hidden';

interface BannerProps {
  type: BannerType;
  children?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  labelClassName?: string;
  label?: string;
  focusOnMount?: boolean;
  icon?: Icon;
}

export function Banner({
  type,
  children,
  className,
  contentClassName,
  labelClassName,
  label,
  focusOnMount = false,
  icon = 'filled',
}: BannerProps) {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusOnMount) {
      bannerRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={bannerRef}
      tabIndex={focusOnMount ? -1 : undefined}
      className={clsx(styles.banner, styles[`${type}Banner`], className)}
    >
      <div className={clsx(styles.content, contentClassName)}>
        <div className={clsx('body-3', styles.iconContainer)}>
          {icon !== 'hidden' && (
            <MaterialSymbolIcon
              name={getIconForCertainType(type)}
              fill={type !== 'lock' && icon === 'filled'}
              className={styles.icon}
            />
          )}
          {label && <label className={clsx(styles.formLabel, labelClassName)}>{label}</label>}
        </div>
        {children && <div className={clsx('body-3', styles.childrenContainer)}>{children}</div>}
      </div>
    </div>
  );
}

function getIconForCertainType(type: BannerType) {
  switch (type) {
    case 'error':
      return 'dangerous';
    case 'success':
      return 'check';
    default:
      return type;
  }
}
